import ThreeDotMenu, {
  type ThreeDotMenuItem,
} from "@/Shared/Components/ThreeDotMenu";
import { AddressBookIcon } from "@phosphor-icons/react/AddressBook";
import { ArrowLeftIcon } from "@phosphor-icons/react/ArrowLeft";
import { ArrowRightIcon } from "@phosphor-icons/react/ArrowRight";
import { BuildingIcon } from "@phosphor-icons/react/Building";
import { PackageIcon } from "@phosphor-icons/react/Package";
import { PencilSimpleIcon } from "@phosphor-icons/react/PencilSimple";
import { StarIcon } from "@phosphor-icons/react/Star";
import { TrashIcon } from "@phosphor-icons/react/Trash";
import { TruckIcon } from "@phosphor-icons/react/Truck";
import { WarehouseIcon } from "@phosphor-icons/react/Warehouse";
import MiniMap from "@/Shared/Maps/MiniMap";
import { Badge } from "@freightsimple/ui-core/Display/Badge";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Spinner } from "@freightsimple/ui-core/Feedback/Spinner";

import { nameAccessorials } from "@/Data/nameAccessorials";
import { describeLocationType } from "@/Data/describeLocationType";
import type { Contact } from "@freightsimple/generated-dashboard-openapi-client/models/Contact";
import type { Location } from "@freightsimple/generated-dashboard-openapi-client/models/Location";
import type { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import { LoadingCapabilitiesAvailability } from "@freightsimple/generated-dashboard-openapi-client/models/LoadingCapabilitiesAvailability";
import { describeHours } from "@/Helpers/describeHours";
import { formatPhone } from "@/Helpers/formatPhone";
import { fullStateOrProvinceName } from "@/Helpers/fullStateOrProvinceName";
import type { ReactNode } from "react";
import {
  describeLoadingCapabilities,
  hasLiftGateAccessorial,
} from "@/Shared/Locations/Helpers/loadingCapabilities";
import { LocationsFavouriteMarker } from "@/Shared/Locations/CustomComponents/LocationsFavouriteMarker";
import { LocationsBranchMarker } from "@/Shared/Locations/CustomComponents/LocationsBranchMarker";

// Replicas of the legacy styled-components. flex + items-center on the
// business name so the name, favourite/branch icons and the default pills
// (all different heights) line up on their vertical centres instead of
// their text baselines.
const businessNameClasses =
  "flex flex-wrap items-center text-[18px] font-medium text-foreground";

function SectionLabel_({ children }: { children: ReactNode }) {
  return <div className="text-xs font-bold text-foreground">{children}</div>;
}

function SectionLine_({ children }: { children: ReactNode }) {
  return (
    <div className="text-xs font-light break-words text-content-muted">
      {children}
    </div>
  );
}

interface LocationBoxMenuProps {
  onModify: () => void;
  onDelete?: (() => void) | undefined;
  isFavourite: boolean;
  isBranch: boolean;
  onToggleFavourite?: (() => void) | undefined;
  onToggleBranch?: (() => void) | undefined;
  isDefaultPickupLocation: boolean;
  isDefaultDeliveryLocation: boolean;
  onToggleDefaultPickupLocation: () => void;
  onToggleDefaultDeliveryLocation: () => void;
  onViewShipmentsFromThisLocation: () => void;
  onViewShipmentsToThisLocation: () => void;
  onChangeContact: () => void;
  rowTestId?: string | undefined;
}

function LocationBoxMenu_(props: LocationBoxMenuProps) {
  const items: ThreeDotMenuItem[] = [];
  items.push({
    name: "Modify",
    icon: PencilSimpleIcon,
    action: function () {
      props.onModify();
    },
  });

  items.push({
    name: "Manage Contacts",
    icon: AddressBookIcon,
    action: function () {
      props.onChangeContact();
    },
  });

  items.push({ name: "Divider" });
  items.push({
    name: props.isFavourite ? "Unmark as favourite" : "Mark as favourite",
    // Star and Building match the markers already drawn on the card itself.
    icon: StarIcon,
    action: function () {
      props.onToggleFavourite?.();
    },
  });

  items.push({
    name: props.isBranch ? "Unmark as branch" : "Mark as branch",
    icon: BuildingIcon,
    action: function () {
      props.onToggleBranch?.();
    },
  });

  items.push({
    name: props.isDefaultPickupLocation
      ? "Unset as default pickup location"
      : "Set as default pickup location",
    icon: TruckIcon,
    action: function () {
      props.onToggleDefaultPickupLocation();
    },
  });

  items.push({
    name: props.isDefaultDeliveryLocation
      ? "Unset as default delivery location"
      : "Set as default delivery location",
    icon: PackageIcon,
    action: function () {
      props.onToggleDefaultDeliveryLocation();
    },
  });

  items.push({ name: "Divider" });

  items.push({
    name: "View Shipments From This Location",
    // Freight leaving here vs arriving here.
    icon: ArrowRightIcon,
    action: function () {
      props.onViewShipmentsFromThisLocation();
    },
  });

  items.push({
    name: "View Shipments To This Location",
    icon: ArrowLeftIcon,
    action: function () {
      props.onViewShipmentsToThisLocation();
    },
  });

  if (props.onDelete) {
    items.push({ name: "Divider" });
    items.push({
      name: "Delete",
      icon: TrashIcon,
      tone: "danger",
      action: function () {
        if (props.onDelete !== undefined) {
          props.onDelete();
        }
      },
    });
  }

  return <ThreeDotMenu items={items} />;
}

function PickupDefault_() {
  return (
    <span className="ml-4">
      <Badge label="Pickup Default" icon={WarehouseIcon} variant="warning" />
    </span>
  );
}

function DeliveryDefault_() {
  return (
    <span className="ml-4">
      <Badge label="Delivery Default" icon={WarehouseIcon} variant="warning" />
    </span>
  );
}

interface LocationBoxProps {
  location: Location;
  contact: Contact | undefined;
  contactCount: number;
  onModify: () => void;
  onChangeContact: () => void;

  showMenu: boolean;
  onDelete?: (() => void) | undefined;
  context: LocationContext;
  isFavourite: boolean;
  isBranch: boolean;
  onToggleFavourite?: (() => void) | undefined;
  onToggleBranch?: (() => void) | undefined;
  isDefaultPickupLocation: boolean;
  isDefaultDeliveryLocation: boolean;
  onToggleDefaultPickupLocation: () => void;
  onToggleDefaultDeliveryLocation: () => void;
  onViewShipmentsFromThisLocation: () => void;
  onViewShipmentsToThisLocation: () => void;
  isLoading?: boolean | undefined;
  locationHasDock?: LoadingCapabilitiesAvailability | undefined;
  locationHasForklift?: LoadingCapabilitiesAvailability | undefined;
  rowTestId?: string | undefined;
}
// oxlint-disable-next-line complexity -- grandfathered, see ENG-7672
function LocationsBox(props: LocationBoxProps) {
  const hasLiftGate = hasLiftGateAccessorial(props.location.accessorials);
  const { businessName, address, notes } = props.location;
  const contact = props.contact;
  const contactCount = props.contactCount;

  const { latitude, longitude } = props.location.latitudeLongitude;
  const { postalCode, stateOrProvinceCode, city } = props.location.address;

  const accessorials = nameAccessorials(
    props.location.accessorials,
    props.context,
    props.location.locationType,
  );

  const hoursDescription = describeHours(props.location.hours);

  return (
    /* Replica of the legacy Cargo Box (Tight shadow). w-full + max-w instead
     * of the old fixed 1004px so the card gives ground on narrow screens. */
    <div
      className={`relative min-h-[242px] w-full max-w-[1004px] rounded-[3px] border border-border-strong p-8 shadow-[var(--color-shadow-ambient)_0px_4px_4px] ${
        props.isLoading ? "pointer-events-none opacity-70" : ""
      }`}
    >
      {props.isLoading && (
        <div className="absolute top-4 right-4">
          <Spinner size={32} />
        </div>
      )}
      <div className="flex h-full flex-row items-start">
        <MiniMap
          latitude={latitude}
          longitude={longitude}
          postalCode={postalCode}
          stateOrProvinceCode={stateOrProvinceCode}
          city={city}
          width={384}
          height={160}
        />

        <div className="w-14 shrink-0" />
        <div
          className="flex flex-col items-start"
          data-testid="address-row"
          data-rowid={props.rowTestId}
        >
          <div className="flex w-full flex-row items-center justify-between">
            <div className={businessNameClasses}>
              {businessName} {props.isFavourite && <LocationsFavouriteMarker />}
              {props.isBranch && <LocationsBranchMarker />}
              {props.isDefaultPickupLocation && <PickupDefault_ />}
              {props.isDefaultDeliveryLocation && <DeliveryDefault_ />}
            </div>
            {props.showMenu && (
              <LocationBoxMenu_
                rowTestId={props.rowTestId}
                onModify={props.onModify}
                onDelete={props.onDelete}
                isFavourite={props.isFavourite}
                isBranch={props.isBranch}
                onToggleFavourite={props.onToggleFavourite}
                onToggleBranch={props.onToggleBranch}
                isDefaultPickupLocation={props.isDefaultPickupLocation}
                isDefaultDeliveryLocation={props.isDefaultDeliveryLocation}
                onToggleDefaultPickupLocation={
                  props.onToggleDefaultPickupLocation
                }
                onToggleDefaultDeliveryLocation={
                  props.onToggleDefaultDeliveryLocation
                }
                onViewShipmentsToThisLocation={
                  props.onViewShipmentsToThisLocation
                }
                onViewShipmentsFromThisLocation={
                  props.onViewShipmentsFromThisLocation
                }
                onChangeContact={props.onChangeContact}
              />
            )}
            {!props.showMenu && (
              <Button size="link" onClick={props.onModify}>
                Modify
              </Button>
            )}
          </div>
          <div className="h-4" />
          <div className="flex w-full flex-row items-start">
            <div className="flex w-[174px] flex-col items-start">
              <SectionLabel_>Address</SectionLabel_>
              <SectionLine_>{address.addressLine}</SectionLine_>
              {address.addressLine2 && (
                <SectionLine_>{address.addressLine2}</SectionLine_>
              )}
              <SectionLine_>{address.city}</SectionLine_>
              <SectionLine_>
                {fullStateOrProvinceName(address.stateOrProvinceCode)}
              </SectionLine_>
              <SectionLine_>{address.postalCode}</SectionLine_>
              <div className="h-2" />
              <SectionLabel_>Default Hours</SectionLabel_>
              <SectionLine_>{hoursDescription}</SectionLine_>
            </div>
            <div className="mr-2 flex w-[180px] flex-col items-start">
              <SectionLabel_>
                {contactCount > 1 ? "Contacts" : "Contact"}
              </SectionLabel_>
              {contact && (
                <>
                  <SectionLine_>{contact.contactName}</SectionLine_>
                  <SectionLine_>{contact.emailAddress}</SectionLine_>
                  <SectionLine_>
                    {formatPhone(
                      contact.phoneNumber,
                      contact.phoneNumberExtension,
                    )}
                  </SectionLine_>
                  {contactCount > 1 && (
                    <>
                      <div className="h-1" />
                      <SectionLine_>(and {contactCount - 1} more)</SectionLine_>
                    </>
                  )}
                </>
              )}
              {!contact && (
                <SectionLine_>There are no saved contacts</SectionLine_>
              )}
              <div className="h-2" />
              <SectionLabel_>Location Type</SectionLabel_>
              <SectionLine_>
                {describeLocationType(
                  props.location.locationType,
                  props.location.distributionWarehouseBrand,
                )}
              </SectionLine_>
            </div>
            <div className="flex w-[138px] flex-col items-start">
              <SectionLabel_>Special Services</SectionLabel_>
              {accessorials.map((a) => {
                return <SectionLine_ key={a}>{a}</SectionLine_>;
              })}
              <div className="h-2" />
              {!hasLiftGate && (
                <>
                  <SectionLabel_>Loading</SectionLabel_>
                  <SectionLine_>
                    {describeLoadingCapabilities(
                      props.locationHasDock ??
                        LoadingCapabilitiesAvailability.Unknown,
                      props.locationHasForklift ??
                        LoadingCapabilitiesAvailability.Unknown,
                    )}
                  </SectionLine_>
                </>
              )}
              <div className="h-2" />
              <SectionLabel_>Notes</SectionLabel_>
              <SectionLine_>{notes || "No notes entered"}</SectionLine_>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
export default LocationsBox;
